import React, { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Button, Select, SelectOption, Tree, TreeNode } from '@freelancelabs/components';

import { ListSkeleton } from '../../../components/Skeletons';
import { DataScope, MembershipStatus, OrganizationKind } from '../../../graphql/__generated__/schema';
import {
    AccessibleAccountsDocument,
    AccessibleAccountsQuery,
    ContactsByEmailDocument,
    RolesDocument,
} from '../../../graphql/__generated__/types';
import PortalAccessDialog from '../../accounts/PortalAccessDialog';
import AttachmentRow from './AttachmentRow';
import { Draft, DraftAttachment } from './draft';
import { refusalRules } from './grantableRoles';

interface Props {
    draft: Draft;
    onChange: (patch: Partial<Draft>) => void;
    /**
     * Back to step 1. One empty case of the pre-ticking: an address no ingested
     * contact carries: is worth re-reading the address for, and it is the only one
     * that offers the trip.
     */
    onEditIdentity: () => void;
}

type Account = NonNullable<NonNullable<AccessibleAccountsQuery['accessibleAccounts']>[number]>;

/**
 * Step 2: the accounts, and what the person carries on each.
 *
 * This is where the mockup and the model part ways. The mockup asks for the accounts
 * here, then **one** role and **one** perimeter for the whole person, later on. Role
 * and perimeter belong to the couple (person × account): they fold into this step,
 * one block per account.
 *
 * The perimeter is a **market scope**, not a list of ticked opportunities and
 * contracts: those do not exist in the model, and a frozen list of activities has
 * to be maintained by hand forever.
 *
 * Only accounts the actor may act on are offered, and it is the same function that
 * bounds the users list: the picker cannot offer an account the invitation would
 * refuse. Two narrowings are applied on top, both of them **this journey's own**:
 *
 * - the **internal** organisation is dropped. Attaching a client-side person to
 *   Freelance.com would make them a colleague, and that journey exists elsewhere
 *   (`inviteCollaborator`). `accessibleAccounts` keeps offering it, because the users
 *   list filters by account and an administrator does target Freelance.com there;
 * - only the accounts of **one** company are listed at all. A person client-side
 *   belongs to a single root organisation (the base enforces it with
 *   `membership_single_root_check`), so the screen must not let a combination be
 *   composed that the invitation will refuse three steps later. Which company that is
 *   comes from the contact, and only failing that from the first tick; the others are
 *   **hidden rather than greyed**, which is the one place this screen departs from the
 *   « désactivé avec sa raison » rule and the reason is argued at `isHidden`.
 *
 * ── Le pré-cochage des comptes (PCG-139)
 *
 * The contact picked at step 1 says where the person **works**, and the step opened
 * with those accounts drowned among every other account of the portfolio, none of
 * them ticked. The same gesture as the markets (PCG-63) is applied here, one level
 * up, and it is held in place by the same three things:
 *
 * - **it says it is one, and why** : the qualifier « désigné par la source » sits on
 *   every pre-ticked row, so a box ticked by a query never reads like a box ticked by
 *   hand, and it stays there once unticked;
 * - **it is an initialisation, not a constraint** : applied once per address, so
 *   unticking sticks and nothing re-ticks behind the operator. Ticking an account the
 *   source does not name stays perfectly legitimate: the source describes where the
 *   person works, not what an administrator decides to open;
 * - **it dies with the address it was resolved for**, in `applyPatch` and not here.
 *   An attachment is what the invitation grants, so one surviving a corrected address
 *   would open somebody an access to another person's accounts.
 *
 * **Attention, the company is anchored on the contact, not on the first tick.** Both because
 * the pre-ticking makes the first tick a consequence of the contact anyway, and
 * because unticking everything must not reopen the whole portfolio: the company a
 * person belongs to is a fact of the source, not of what is ticked on this screen.
 * The gesture that changes it is therefore step 1, and the note says so.
 */
const AttachmentsStep = ({ draft, onChange, onEditIdentity }: Props) => {
    /** The account whose opening is being confirmed, or null. */
    const [opening, setOpening] = useState<Account | null>(null);
    /** The closed account picked as the target of the opening, before confirming it. */
    const [toOpenId, setToOpenId] = useState<string | null>(null);

    const {
        data: accountsData,
        loading: accountsLoading,
        error: accountsError,
        refetch: refetchAccounts,
    } = useQuery(AccessibleAccountsDocument);
    const allAccounts = (accountsData?.accessibleAccounts ?? []).filter(Boolean);
    const accounts = allAccounts.filter(
        (account): account is Account => Boolean(account.id) && account.kind !== OrganizationKind.Internal
    );

    /**
     * L'adresse de l'étape 1, **rigoureusement la même chaîne** : c'est elle qui sert
     * de variable à la requête relue ci-dessous, et de génération au pré-cochage.
     */
    const email = draft.email.trim();

    /**
     * **La réponse de l'étape 1, relue ici pour DEUX faits qu'il ne faut pas
     * confondre** (PCG-109, PCG-111, puis PCG-139).
     *
     * **Attention, les deux listes de la réponse ne disent pas la même chose, et tout ce lot
     * tient sur cette distinction** : `existingAttachments` dit les comptes où la
     * personne a **déjà un accès au portail**, `contact.accounts` dit les sociétés où
     * la source la fait **travailler**. La première grise, la seconde pré-coche, et
     * quand un compte est dans les deux **c'est le grisé qui l'emporte** : pré-cocher
     * y enverrait une invitation que `MEMBERSHIP_ALREADY_EXISTS` refuse.
     *
     * Le grisé, d'abord (PCG-109 puis PCG-111). Jusque-là l'étape laissait cocher, et
     * `inviteUser` refusait **au dernier écran**, après que l'opérateur avait composé
     * le rôle, le périmètre de marchés, les tableaux de bord et la liste de
     * partenaires. C'est la règle « un choix qu'on ne peut pas faire est désactivé
     * avec sa raison » qui n'était pas tenue, faute d'avoir le fait sous la main.
     *
     * **Attention, relu ici plutôt que porté par le brouillon.** L'étape 1 a déjà fait cette
     * requête, donc le cache d'Apollo répond sans aller au serveur : à condition que
     * la variable soit la MÊME, d'où le `trim()`, que l'étape 1 applique aussi. Le
     * ranger dans le brouillon aurait ajouté une chose de plus à périmer quand
     * l'adresse change ; ce qui y est rangé est le **résultat** du pré-cochage, qui
     * doit survivre à un démontage de l'étape, et non la réponse elle-même.
     */
    const {
        data: contactData,
        loading: contactLoading,
        error: contactError,
    } = useQuery(ContactsByEmailDocument, {
        variables: { email },
        skip: email.length === 0,
    });
    const attachedStatusById = new Map(
        (contactData?.contactsByEmail?.existingAttachments ?? []).map((attachment) => [
            attachment.organizationId,
            attachment.status,
        ])
    );

    const { data: rolesData, error: rolesError, refetch: refetchRoles } = useQuery(RolesDocument);
    // Portal scope only: a back-office role on a client account would give the person
    // the administration screens.
    const roles = (rolesData?.portal ?? []).filter(Boolean);

    const selectedIds = draft.attachments.map((attachment) => attachment.organizationId);
    const byId = new Map(accounts.map((account) => [account.id as string, account]));

    /**
     * **Les comptes que la SOURCE désigne pour la personne choisie à l'étape 1.**
     *
     * Le contact et non les contacts : une boîte partagée en désigne plusieurs, et
     * pré-cocher les comptes de tous mélangerait deux personnes. C'est `draft.contact`
     * qui dit lequel a été adopté, et `applyPatch` garantit qu'il porte l'adresse
     * courante, donc trouver la même identité dans une réponse restée à l'écran pour
     * une autre adresse est impossible sans que ce soit le même contact.
     */
    const pickedContactId = draft.contact?.id;
    const pickedContact =
        contactData?.contactsByEmail?.contacts.find((contact) => contact.id === pickedContactId) ?? null;
    const sourceAccounts = pickedContactId ? (pickedContact?.accounts ?? []) : [];
    const sourceById = new Map(sourceAccounts.map((account) => [account.id, account]));

    /** Ceux de ces comptes que l'arbre offre. Les autres sont dits, jamais tus. */
    const designated = sourceAccounts
        .map((source) => byId.get(source.id))
        .filter((account): account is Account => Boolean(account));

    /**
     * The company every account has to belong to, or null while nothing pins it.
     *
     * **Attention, it comes from the contact first, and from the ticks only failing that.**
     * Anchoring it on the first tick left the whole portfolio open until something was
     * ticked, which, now that the source ticks first, would only ever happen again by
     * unticking everything. Reopening the portfolio there would be the wrong reading:
     * an empty selection means « rien de décidé », never « cette personne n'appartient
     * plus à sa société ».
     *
     * Two narrowings on what may anchor, and both are the same rule as ever. An
     * account whose root is null says nothing about the company, so it does not
     * anchor. And **several roots anchor nothing**: the source may attach a contact to
     * two companies (the mirror allows it where `membership_single_root_check` does
     * not), so the screen has no way of electing one, and says so rather than picking.
     * `isPrimary` is not that tie-break: the API writes it as display-only, « ne
     * pilote aucun droit, et n'est pas une valeur par défaut de l'étape 2 ».
     */
    const designatedRoots = [...new Set(designated.map((account) => account.rootOrganizationId).filter(Boolean))];
    const acrossCompanies = designatedRoots.length > 1;
    const contactAnchor =
        designatedRoots.length === 1
            ? (designated.find((account) => account.rootOrganizationId === designatedRoots[0]) ?? null)
            : null;
    const anchor =
        contactAnchor ?? selectedIds.map((id) => byId.get(id)).find((account) => account?.rootOrganizationId);
    const lockedRootId = anchor?.rootOrganizationId ?? null;
    const lockedRootName = anchor?.rootOrganizationName ?? null;

    /**
     * A node is refused when we can prove it belongs to another company. A null root
     * is not that proof (it signals a loop in the parenting chain) so those accounts
     * stay pickable, and the invitation refuses them with its own message rather than
     * this screen hiding a data defect behind a missing option.
     */
    const isRefused = (account: Account) =>
        Boolean(lockedRootId && account.rootOrganizationId && account.rootOrganizationId !== lockedRootId);

    /**
     * An account closed to the portal makes the invitation fail at the very last step,
     * after the role and the perimeter have been chosen. Same reading as above: only
     * `false` is proof, a null says the API did not tell us, and an account that says
     * nothing stays pickable, so a missing field never looks like a closed account.
     *
     * Opening an account is a business decision; the import is forbidden from writing
     * it and no screen carries it yet, so this is today the only place where an
     * operator learns that the account has to be opened first.
     */
    const isClosedToPortal = (account: Account) => account.portalEnabled === false;

    /**
     * The accounts this journey could use but for their portal being closed: the ones
     * whose refusal a gesture can lift. Accounts of another company are left out: their
     * refusal is not the portal, and offering to open one would nudge the operator
     * towards a combination the invitation refuses anyway.
     */
    /**
     * Attention, les comptes où la personne est déjà rattachée en sont exclus aussi : ouvrir
     * un compte au portail pour y ajouter quelqu'un qui y est déjà est un geste sans
     * effet, et l'offrir le fait passer pour la solution. Lu sur la table plutôt que
     * par `isAlreadyAttached`, qui est défini plus bas.
     */
    const closedAccounts = accounts.filter(
        (account) => isClosedToPortal(account) && !isRefused(account) && !attachedStatusById.has(account.id as string)
    );

    const closedOptions: SelectOption<string>[] = closedAccounts.map((account) => ({
        label: account.name ?? '',
        value: account.id as string,
    }));
    const toOpen = closedAccounts.find((account) => account.id === toOpenId) ?? null;

    /**
     * **Attention, les quatre statuts refusent l'invitation**, donc aucun ne reste cochable :
     * `MEMBERSHIP_ALREADY_EXISTS` ne regarde pas le statut, c'est l'unique
     * `(user_id, organization_id)` qui tient. Mais ils n'appellent pas le même geste,
     * et le qualificatif le dit : `INVITED`/`ACTIVE` se règlent depuis la fiche de la
     * personne, `REVOKED`/`LEFT` demandent une réouverture. Écrire « déjà rattachée »
     * sur un accès retiré enverrait chercher un rôle à changer sur une ligne éteinte.
     */
    const attachmentOf = (account: Account) => attachedStatusById.get(account.id as string);
    const isAlreadyAttached = (account: Account) => attachmentOf(account) !== undefined;

    const refusalOf = (account: Account) => {
        // En premier, parce que c'est un fait sur la PERSONNE et qu'il rend les deux
        // autres sans objet : proposer d'ouvrir un compte où elle est déjà rattachée
        // serait pousser l'opérateur vers un geste inutile.
        const status = attachmentOf(account);
        if (status === MembershipStatus.Revoked || status === MembershipStatus.Left) {
            return c('Onboarding').t`Access to reopen`;
        }
        if (status !== undefined) {
            return c('Onboarding').t`Already attached`;
        }
        // Attention, ce qualificatif n'annote plus AUCUNE ligne de l'arbre depuis le masquage :
        // une ligne refusée pour sa société n'y est plus rendue, sauf cochée ou déjà
        // rattachée, et ces deux-là sont nommées avant. Il ne sert donc plus qu'aux
        // comptes que la SOURCE désigne et que l'arbre n'offre pas, listés nommément
        // sous leur propre note. C'est le partage voulu : le refus se lit à côté d'un
        // compte qu'on a nommé, jamais en face de trente lignes qu'on a fait défiler.
        if (isRefused(account)) {
            return c('Onboarding').t`Another company`;
        }
        if (isClosedToPortal(account)) {
            return c('Onboarding').t`Not open to the portal`;
        }
        return undefined;
    };

    /**
     * **Attention, les comptes des autres sociétés sont MASQUÉS, et ce n'est pas un abandon de
     * la règle du dépôt** (PCG-139, arbitré par Mohamed sur l'écran). Le prochain
     * lecteur y verra une régression : voici pourquoi ce n'en est pas une.
     *
     * « Un choix impossible se désactive avec sa raison plutôt que de disparaître »
     * vaut quand l'ensemble refusé est **petit et instructif** : deux rôles sur sept,
     * quatre modules sur six. Ici l'impossibilité est **structurelle et totale** :
     * une personne n'appartient qu'à une seule société, `membership_single_root_check`
     * l'impose, donc **tous** les comptes des autres sociétés sont refusés, sans
     * exception et pour toujours. En base de dev, 33 lignes grisées sur 69 ; sur le
     * parc réel ce serait des milliers de refus pour en proposer une poignée. Un écran
     * fait de refus n'informe plus, et le titre de PCG-60 le demandait déjà : « ne
     * proposer que les comptes de la société de la personne ». La règle est donc dite
     * **une fois au-dessus de l'arbre**, forme déjà retenue pour les modules non
     * livrés et pour les droits d'un module non livré.
     *
     * **Attention, deux exceptions, et elles ne sont pas décoratives.**
     *
     * - **On ne masque que si l'ancre est connue.** Sans elle, `isRefused` est faux
     *   partout et l'arbre reste entier : masquer sur une ancre absente rendrait un
     *   écran vide sans explication, exactement le défaut qu'on évite. C'est le cas du
     *   contact qui ne désigne aucun compte de l'arbre, et celui de la source qui en
     *   désigne dans plusieurs sociétés. L'écran dit alors pourquoi tout est proposé.
     * - **Une ligne cochée, ou déjà rattachée, ne se masque JAMAIS.** Cochée, elle
     *   quitterait l'arbre en restant dans `draft.attachments` : la case n'aurait plus
     *   où se décocher. Déjà rattachée, elle porte l'information la plus utile de
     *   l'écran (« passez par sa fiche », « rouvrez l'accès »), et c'est justement le
     *   grisé que ce lot conserve, le petit nombre et l'autre famille. Les deux se
     *   produisent hors de l'ancre : une adresse corrigée déplace la société sous des
     *   rattachements cochés à la main, et une personne peut déjà avoir un accès dans
     *   une société que la source ne lui donne plus.
     */
    const isTicked = (account: Account) => selectedIds.includes(account.id as string);
    const isHidden = (account: Account) => isRefused(account) && !isTicked(account) && !isAlreadyAttached(account);

    /**
     * **Attention, un compte DÉJÀ COCHÉ ne se désactive pas, même devenu impossible.**
     * `applyPatch` ne retire, quand l'adresse change, que ce qu'une requête avait posé
     * pour l'adresse précédente : les marchés suggérés et les comptes pré-cochés. Ce
     * que l'opérateur a coché à la main **reste**, c'est une décision. Donc
     * « Modifier » depuis le récapitulatif, une autre adresse, et le compte coché à la
     * main appartient maintenant à quelqu'un qui y est déjà. Désactivé, il resterait
     * **coché sans pouvoir être décoché** : la ligne piège exactement l'opérateur
     * qu'elle prétend guider, et l'envoi refuserait sur un choix qu'il n'avait plus le
     * droit de défaire.
     *
     * La règle du dépôt le dit dans ce sens : le choix reste visible, ce qui a changé
     * est dit à côté, et l'envoi refuse. Le qualificatif s'affiche donc dans les deux
     * cas ; seule la case reste ouverte quand elle est déjà cochée.
     *
     * **Attention, le commentaire disait ça et le code ne le faisait qu'à moitié**, ce qui ne
     * se voyait pas tant que l'ancre venait des cases : un compte coché partageait
     * alors forcément la racine de l'ancre, donc `isRefused` ne pouvait pas le viser.
     * Depuis que le contact ancre (PCG-139), un rattachement coché à la main survit à
     * un changement d'adresse et se retrouve dans une autre société : la ligne était
     * cochée, désactivée, indécochable, et l'envoi refusait dessus. D'où la garde
     * unique ci-dessous : **cochée, la ligne reste active**, quelle que soit la raison
     * qui la refuse, et cette raison s'affiche à côté.
     */
    /**
     * Ce que l'ARBRE montre coché, qui n'est pas ce que l'invitation ENVERRA.
     *
     * Attention, une case vide sur « Déjà rattachée » dit le contraire du fait : elle se lit
     * « cette personne n'y est pas », alors qu'elle y est. Et elle rendait les deux
     * refus identiques à l'œil : un compte fermé au portail est gris et vide parce
     * que la personne n'y est **pas** rattachée, et c'est exactement la distinction
     * que l'opérateur doit voir sans la déduire. Cochée et désactivée, la ligne dit
     * « déjà dedans, et pas modifiable ici » ; vide et désactivée, « pas dedans, et
     * pas possible ici ».
     *
     * **Attention, ces identifiants n'entrent JAMAIS dans le brouillon.** `draft.attachments`
     * reste ce qui part dans `inviteUser` : les ajouter y enverrait une invitation
     * pour un rattachement existant, c'est-à-dire exactement le refus que ce lot
     * supprime. Deux listes, deux rôles, et c'est pour ça qu'elles portent deux noms.
     */
    const tickedInTree = [...selectedIds, ...attachedStatusById.keys()];

    const toNode = (account: Account, level: string): TreeNode => {
        const source = sourceById.get(account.id as string);
        return {
            id: account.id as string,
            label: account.name ?? '',
            // Le niveau dans l'arbre, puis ce que la source dit du compte. Deux faits
            // de natures différentes, donc deux qualificatifs : le premier parle de
            // l'arborescence, le second de la personne. Et il reste après décochage :
            // c'est ce que l'opérateur pèse pour recocher.
            badges: [
                level,
                ...(source
                    ? [
                          c('Onboarding account origin').t`Designated by the source`,
                          ...(source.isPrimary ? [c('Onboarding account origin').t`Main company`] : []),
                      ]
                    : []),
            ],
            disabled:
                !isTicked(account) && (isAlreadyAttached(account) || isRefused(account) || isClosedToPortal(account)),
            disabledReason: refusalOf(account),
        };
    };

    /**
     * The tree, grouped by company. Depth stops at two on purpose: `accessibleAccounts`
     * is a **perimeter**, not a tree: it carries the root of each account, never its
     * parent, so « master account » and « child account » is all the shape we can
     * honestly draw. The mockup's deeper nesting would be invented.
     *
     * The root is often absent from the list it heads: a portfolio may hold a
     * subsidiary without holding the group, which is why its name travels with the
     * accounts instead of being looked up here. When it is absent, it heads the group
     * without being a choice.
     *
     * Le masquage se pose **ici et sur rien d'autre** : les refus, les qualificatifs et
     * l'ancre continuent de se calculer sur `accounts` entier. Un compte masqué n'est
     * pas un compte qui a cessé d'être refusé, c'est un refus qu'on ne fait plus lire
     * ligne à ligne, et le jour où l'ancre disparaît l'arbre revient tel quel, sans
     * qu'aucun autre calcul n'ait bougé. Un groupe dont tous les membres sont masqués
     * s'en va avec eux, en-tête compris.
     */
    const buildNodes = (): TreeNode[] => {
        const groups = new Map<string, Account[]>();
        const rootless: Account[] = [];

        accounts
            .filter((account) => !isHidden(account))
            .forEach((account) => {
                if (!account.rootOrganizationId) {
                    rootless.push(account);
                    return;
                }
                const members = groups.get(account.rootOrganizationId) ?? [];
                members.push(account);
                groups.set(account.rootOrganizationId, members);
            });

        const grouped = [...groups.entries()]
            .map(([rootId, members]) => {
                const rootAccount = members.find((account) => account.id === rootId);
                const children = members
                    .filter((account) => account.id !== rootId)
                    .map((account) => toNode(account, c('Onboarding account level').t`Child account`));
                const rootName = members[0].rootOrganizationName ?? c('Onboarding').t`Company without a name`;

                if (rootAccount) {
                    return {
                        ...toNode(rootAccount, c('Onboarding account level').t`Master account`),
                        children,
                    };
                }
                return {
                    id: `group-${rootId}`,
                    label: rootName,
                    selectable: false,
                    children,
                };
            })
            .sort((left, right) => String(left.label).localeCompare(String(right.label)));

        return [
            ...grouped,
            // No company at all: kept last, and named for what it is. Silently dropping
            // them would make a malformed parenting chain look like an empty portfolio.
            ...rootless.map((account) => toNode(account, c('Onboarding account level').t`Company unknown`)),
        ];
    };

    /**
     * Why the tree is empty, or null when it is not. The order matters: a failed
     * query and a query still in flight both leave it empty, and neither of them
     * means « no account ».
     */
    const emptyReason = (): string | null => {
        if (accountsError) {
            return c('Onboarding').t`The accounts could not be loaded.`;
        }
        if (accountsLoading) {
            return c('Onboarding').t`Loading the accounts…`;
        }
        if (allAccounts.length === 0) {
            return c('Onboarding').t`No account you can act on.`;
        }
        if (accounts.length === 0) {
            // The internal organisation was the only thing there. Saying « aucun compte »
            // would send the operator hunting for a permission problem that is not one.
            return c('Onboarding')
                .t`The only organisation you can act on is Freelance.com, which is not a client account. Onboarding a colleague goes through the collaborator journey.`;
        }
        return null;
    };

    const accountName = (organizationId: string) => byId.get(organizationId)?.name ?? organizationId;

    const newAttachment = (organizationId: string): DraftAttachment => ({
        organizationId,
        roleId: null,
        // The API takes no default on purpose: a « whole account » posted by
        // omission is indistinguishable from one that was decided. We show it
        // pre-selected, which is a choice made on screen, not a silent one.
        dataScope: DataScope.All,
        needIds: [],
        serviceIds: [],
        // Nothing suggested yet: the query only runs once the perimeter is
        // restricted, and asking it for every added account would ask about
        // accounts nobody is going to restrict.
        suggestion: null,
    });

    const addAccounts = (organizationIds: string[]) =>
        onChange({ attachments: [...draft.attachments, ...organizationIds.map(newAttachment)] });

    /**
     * **Le pré-cochage, appliqué une fois par adresse**, et c'est toute sa sûreté.
     *
     * Les deux requêtes sont attendues, et pour deux raisons distinctes : sans les
     * comptes accessibles il n'y a ni racine ni refus à lire, donc rien pour décider
     * quoi cocher ; sans la réponse de l'étape 1 il n'y a pas de contact. Une requête
     * **en échec** n'écrit pas d'instantané, si bien qu'un « Réessayer » initialise
     * encore.
     *
     * **Attention, L'instantané s'écrit même quand rien n'est coché**, et c'est ce qui
     * distingue « le pré-cochage a eu lieu et n'a rien trouvé » de « il n'a pas encore
     * eu lieu ». Sans lui l'effet reposerait la question à chaque rendu, et surtout un
     * décochage complet serait aussitôt défait.
     *
     * Ce qui n'est PAS coché, et chaque exclusion a sa raison : un compte où la
     * personne a déjà un accès (le grisé l'emporte, et l'invitation le refuserait), un
     * compte fermé au portail (même refus, un cran plus loin), un compte d'une autre
     * société que l'ancre, et, cas entier, **rien du tout quand la source désigne
     * plusieurs sociétés**, puisque la sélection serait alors refusée en bloc.
     *
     * **Attention, aucune liste de dépendances, et c'est délibéré.** Tout ce que l'effet lit
     * est reconstruit à chaque rendu (les comptes filtrés, les prédicats de refus, la
     * sélection), donc une liste serait une affirmation fausse plutôt qu'une
     * optimisation. Ce qui arrête l'effet est sa garde, pas React : elle est vraie dès
     * l'instantané écrit, et l'instantané l'est toujours.
     */
    useEffect(() => {
        if (accountsLoading || contactLoading || accountsError || contactError) {
            return;
        }
        if (!pickedContact || draft.accountSuggestion?.email === email) {
            return;
        }
        const preTicked = acrossCompanies
            ? []
            : designated.filter(
                  (account) =>
                      !isAlreadyAttached(account) &&
                      !isClosedToPortal(account) &&
                      !isRefused(account) &&
                      !selectedIds.includes(account.id as string)
              );
        const accountIds = preTicked.map((account) => account.id as string);
        onChange({
            attachments: [...draft.attachments, ...accountIds.map(newAttachment)],
            accountSuggestion: { email, accountIds },
        });
    });

    const patchAttachment = (organizationId: string, patch: Partial<DraftAttachment>) =>
        onChange({
            attachments: draft.attachments.map((attachment) =>
                attachment.organizationId === organizationId ? { ...attachment, ...patch } : attachment
            ),
        });

    /**
     * Dropping an account drops the dashboards that hung from it, in the same gesture.
     *
     * Not tidying-up: a dashboard whose account has left the selection is refused by
     * `inviteUser` with `FORBIDDEN_OUT_OF_SCOPE` at the send: the very last screen,
     * once the role and the perimeter have been composed. Keeping it would let the
     * operator untick an account here and fail three steps later on a dashboard they
     * can no longer see. Same idiom as leaving a restricted perimeter, which drops its
     * markets rather than sending ones the API ignores.
     */
    const removeAttachment = (organizationId: string) =>
        onChange({
            attachments: draft.attachments.filter((attachment) => attachment.organizationId !== organizationId),
            dashboards: draft.dashboards.filter((dashboard) => dashboard.organizationId !== organizationId),
        });

    const count = draft.attachments.length;

    /**
     * L'instantané écrit pour l'adresse courante, ou null tant qu'il n'y en a pas.
     *
     * Tout ce que l'écran DIT du pré-cochage se lit ici, jamais sur la requête : la
     * phrase et les cases ont été écrites dans le même geste, elles ne peuvent donc
     * pas raconter deux histoires. Et le compte est celui de ce qui a été **proposé**,
     * au passé : il reste juste après que l'opérateur a tout décoché.
     */
    const applied = draft.accountSuggestion?.email === email ? draft.accountSuggestion : null;
    const preTickedCount = applied?.accountIds.length ?? 0;

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h2 className="text-sm font-semibold text-text-900">{c('Onboarding').t`Account selection`}</h2>
            </div>

            {/* A query that fell over is not an account that does not exist. Left to the
                empty state below, the operator reads « rien à rattacher » and moves on. */}
            {accountsError && (
                <div className="flex flex-wrap items-center gap-3 text-sm text-danger-600" role="alert">
                    <span>
                        {c('Onboarding')
                            .t`The accounts could not be loaded. The tree below is empty for want of an answer, not for want of accounts.`}
                    </span>
                    <Button category="weak" size="small" onClick={() => refetchAccounts()}>
                        {c('Action').t`Retry`}
                    </Button>
                </div>
            )}

            {/* Ce que la source a coché, dit au PASSÉ : la phrase décrit ce qui a été
                proposé, ce qui reste vrai une fois l'opérateur passé derrière. « N
                comptes sont pré-cochés » au-dessus d'un arbre où plus rien ne l'est
                contredirait les cases juste en dessous. */}
            {preTickedCount > 0 && (
                <p
                    className="rounded-lg border border-admin-accent-border bg-admin-accent-soft px-3 py-2 text-sm text-text-600"
                    role="status"
                >
                    {c('Onboarding suggestion').ngettext(
                        msgid`${preTickedCount} account was pre-ticked to start with, because the source attaches this person to it. Untick whatever should not be granted, and tick whatever the source does not name: it says where the person works, not what you decide to open.`,
                        `${preTickedCount} accounts were pre-ticked to start with, because the source attaches this person to them. Untick whatever should not be granted, and tick whatever the source does not name: it says where the person works, not what you decide to open.`,
                        preTickedCount
                    )}
                </p>
            )}

            {/* La source désigne deux sociétés, donc l'écran n'en élit aucune. Cocher
                les deux serait composer une sélection que l'invitation refuse en bloc,
                et choisir pour l'opérateur serait pire : rien ne dit laquelle est la
                bonne. */}
            {acrossCompanies && (
                <p
                    className="rounded-lg border border-admin-accent-border bg-admin-accent-soft px-3 py-2 text-sm text-warning-700"
                    role="alert"
                >
                    {c('Onboarding suggestion')
                        .t`The source attaches this person to accounts of several companies, so nothing was pre-ticked and every company is listed below: client-side a person belongs to a single one, and the invitation would refuse the lot. Tick the accounts of the right company, and the others leave the list.`}
                </p>
            )}

            {/* **La règle du masquage, dite UNE FOIS, et c'est ce qui remplace les 33
                lignes grisées.** Un arbre qui s'est raccourci sans un mot se lit comme
                un arbre incomplet, et l'opérateur n'a alors aucun moyen de savoir si le
                compte qu'il cherche est refusé ou absent du référentiel.

                Deux phrases, parce que ce n'est pas le même fait qui restreint et donc
                pas le même geste qui rouvre : l'ancre du contact ne se défait pas en
                décochant (la société d'une personne ne dépend pas des cases de cet
                écran), là où celle d'un premier clic, elle, se défait au décochage. */}
            {lockedRootName && (
                <div
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-admin-accent-border bg-admin-accent-soft px-3 py-2 text-sm text-text-600"
                    role="status"
                >
                    {contactAnchor ? (
                        <>
                            <span>
                                {c('Onboarding')
                                    .t`Only the accounts of ${lockedRootName} are listed: this is the company the source attaches this person to, and client-side a person belongs to a single one, so the other companies are not shown here. If it is not the right company, it is the contact that has to change, not the boxes below.`}
                            </span>
                            <Button category="weak" size="small" onClick={onEditIdentity}>
                                {c('Onboarding action').t`Check the address`}
                            </Button>
                        </>
                    ) : (
                        <span>
                            {c('Onboarding')
                                .t`Only the accounts of ${lockedRootName} are listed, the first tick having settled the company: client-side, a person belongs to a single one. Untick every account to see the whole tree again.`}
                        </span>
                    )}
                </div>
            )}

            {/* Pourquoi TOUT est proposé, quand rien n'ancre. Le pendant de la note du
                dessus, et il lui faut sa propre phrase : sans ancre, un arbre entier au
                milieu d'un parcours qui vient d'annoncer une seule société se lirait
                comme un masquage en panne.

                Muette pendant que ça charge et après un échec : « la source ne désigne
                aucune société » serait alors une affirmation tirée d'une absence de
                réponse. Muette aussi sur plusieurs sociétés, cas qui a sa note à lui. */}
            {!lockedRootName && !acrossCompanies && pickedContact && !accountsLoading && !accountsError && (
                <p
                    className="rounded-lg border border-admin-accent-border bg-admin-accent-soft px-3 py-2 text-sm text-text-600"
                    role="status"
                >
                    {c('Onboarding')
                        .t`The source names no company for this person among the accounts you administer, so all of them are listed. The first account ticked settles the company, and the others then leave the list: client-side, a person belongs to a single company.`}
                </p>
            )}

            <div className="max-h-80 overflow-auto rounded-lg border border-border-norm px-2 py-2">
                {/*
                    LE SQUELETTE PREND LA PLACE DE L'ARBRE, il ne passe pas par
                    `emptyLabel`. Cette prop dit « il n'y a rien », ce qui est une
                    affirmation sur le perimetre de l'acteur ; en chargement on ne sait
                    pas encore. Et un cadre haut de 320 px portant une phrase centree
                    puis soixante-neuf lignes est le saut que ce lot corrige partout.
                */}
                {accountsLoading ? (
                    <ListSkeleton rows={7} withControl />
                ) : (
                    <Tree
                        nodes={buildNodes()}
                        selected={tickedInTree}
                        onToggle={(id, checked) => (checked ? addAccounts([id]) : removeAttachment(id))}
                        onSelectBranch={addAccounts}
                        branchActionLabel={c('Onboarding action').t`Tick its child accounts too`}
                        label={c('Onboarding').t`Account tree`}
                        emptyLabel={emptyReason()}
                    />
                )}
            </div>

            {/* The remedy, next to the diagnosis. The tree already greys these accounts
                with « Compte non ouvert au portail »; without this block the operator
                reads the reason and has nowhere to go.

                It is a block **under** the tree and not a control inside a row: the row
                is a `treeitem` marked `aria-disabled`, and a button nested in one is
                announced as part of a disabled node: plus the tree toggles selection on
                click, so the gesture would fight the selection.

                Opening in place, rather than a link to the account's own screen: the
                draft lives in component state, so navigating away to open the account
                would throw away the identity and the attachments already entered. */}
            {closedAccounts.length > 0 && (
                <div className="rounded-lg border border-border-norm bg-background-weak px-3 py-2.5" role="status">
                    {/* One button and a target to pick, not one button per account. A
                        portfolio holds tens of closed accounts: the first version printed
                        29 buttons under a tree of 5 rows, and the remedy drowned the screen
                        it was annotating. The select names the target; the gesture itself
                        stays a single labelled button, and its confirmation is the dialog. */}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Select
                            options={closedOptions}
                            value={closedOptions.find((option) => option.value === toOpenId)}
                            onChange={(option) => setToOpenId(option.value)}
                            placeholder={c('Onboarding').t`Pick an account to open`}
                            aria-label={c('Onboarding').t`Account to open to the portal`}
                            className="max-w-xs"
                        />
                        <Button
                            category="weak"
                            size="small"
                            disabled={!toOpen}
                            onClick={() => toOpen && setOpening(toOpen)}
                        >
                            {c('Onboarding action').t`Open this account to the portal…`}
                        </Button>
                    </div>
                </div>
            )}

            {opening?.id && (
                <PortalAccessDialog
                    accountId={opening.id}
                    accountName={opening.name ?? opening.id}
                    direction="open"
                    onClose={() => {
                        setOpening(null);
                        // Once opened, the account leaves `closedAccounts`; a stale id
                        // would leave the select showing a value it no longer offers.
                        setToOpenId(null);
                    }}
                />
            )}

            <p className="text-xs text-text-600">
                {ngettext(msgid`${count} account selected.`, `${count} accounts selected.`, count)}
            </p>

            {/* Same silence one step further: without the roles, every account added here
                stays blocked on « Choisissez un rôle » with nothing to choose from. */}
            {rolesError && (
                <div className="flex flex-wrap items-center gap-3 text-sm text-danger-600" role="alert">
                    <span>{c('Onboarding').t`The roles could not be loaded, so no role can be picked below.`}</span>
                    <Button category="weak" size="small" onClick={() => refetchRoles()}>
                        {c('Action').t`Retry`}
                    </Button>
                </div>
            )}

            {/* Why the pickers below hold rows that refuse: said **once**, above them,
                and not on each account. The refusal is a fact about the operator, not
                about the account, so the same sentence would repeat verbatim on every
                row; the same reason the remedy for the closed accounts is one block
                under the tree rather than one button per row.

                Kept as a `status` and not an alert: nothing failed, the referential is
                complete and the answer is the right one. What each greyed row refuses
                for is tagged on the row itself. */}
            {refusalRules(roles).map((rule) => (
                <p
                    key={rule}
                    className="rounded-lg border border-admin-accent-border bg-admin-accent-soft px-3 py-2 text-sm text-text-600"
                    role="status"
                >
                    {rule}
                </p>
            ))}

            {draft.attachments.length === 0 ? (
                // Silent when the query failed: the alert above already says what is
                // going on, and « aucun compte pour l'instant » would soften it into a
                // normal state.
                !accountsError && (
                    <p className="rounded-lg border border-dashed border-border-norm px-4 py-6 text-center text-xs text-text-600">
                        {c('Onboarding')
                            .t`No account yet. A person without an attachment would sign in and see nothing.`}
                    </p>
                )
            ) : (
                <ul className="flex flex-col gap-3">
                    {draft.attachments.map((attachment) => (
                        <AttachmentRow
                            key={attachment.organizationId}
                            attachment={attachment}
                            accountName={accountName(attachment.organizationId)}
                            email={draft.email.trim()}
                            roles={roles}
                            onEditIdentity={onEditIdentity}
                            onChange={(patch) => patchAttachment(attachment.organizationId, patch)}
                            onRemove={() => removeAttachment(attachment.organizationId)}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AttachmentsStep;
